import { useNavigate } from 'react-router-dom';

import { Dropdown, DropdownDivider, DropdownItem, IconButton } from '../common';
import { useAuth } from '../../context/AuthContext';
import { useModel } from '../../context/ModelContext';
import { useTheme } from '../../context/ThemeContext';
import { VersionPill } from './VersionPill';
import './layout.css';

/**
 * Title, version pill, GPU state, theme toggle.
 *
 * The GPU chip reports used/total VRAM and the adapter parameter count, because
 * those two numbers answer the two questions that actually come up: is the card
 * full, and did the adapter really load.
 */
export function TopBar({ title, children, showVersion = true }) {
  const { worker, activeVersion, isVllm, serving } = useModel();
  const { resolved, toggle } = useTheme();
  const { user, organization, isAdmin, logout } = useAuth();
  const navigate = useNavigate();

  // Under vLLM the supervisor reads the whole card (nvidia-smi), which is the
  // number that matters when several processes share it.
  const gpu = serving?.gpu || {};
  const vram =
    isVllm && gpu.memory_total_mb > 0
      ? `${(gpu.memory_used_mb / 1024).toFixed(1)} / ${(gpu.memory_total_mb / 1024).toFixed(0)} GB`
      : worker?.vram_total_gb > 0
        ? `${worker.vram_used_gb.toFixed(1)} / ${worker.vram_total_gb.toFixed(0)} GB`
        : null;
  const servingCount = (serving?.servers || []).filter((s) => s.state === 'ready').length;

  return (
    <header className="topbar">
      {title && <div className="topbar__title">{title}</div>}
      {showVersion && <VersionPill />}
      {children}

      <div className="topbar__spacer" />

      {worker?.error && (
        <span className="chip chip--warn" title={worker.error}>
          worker offline
        </span>
      )}
      {isVllm && serving && (
        <span
          className={`chip ${serving.model_server?.reachable ? '' : 'chip--warn'}`}
          title={
            serving.model_server?.reachable
              ? `${serving.policy?.mode === 'multi' ? 'Multi-model' : 'Single-model'} serving`
              : serving.model_server?.error || 'Model server not reachable'
          }
        >
          vLLM · {servingCount} model{servingCount === 1 ? '' : 's'}
        </span>
      )}
      {vram && (
        <span
          className="chip"
          title={
            isVllm && gpu.name
              ? gpu.name
              : `${worker?.gpu_name || worker?.device} · engine ${worker?.engine}`
          }
        >
          GPU {vram}
        </span>
      )}
      {!isVllm && activeVersion && worker?.adapter_params > 0 && (
        <span className="chip" title="LoRA tensors copied into the live model">
          {worker.adapter_params} adapter params
        </span>
      )}
      <IconButton
        label={resolved === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
        icon={resolved === 'dark' ? '☀' : '☾'}
        onClick={toggle}
      />

      <AccountMenu
        user={user}
        organization={organization}
        isAdmin={isAdmin}
        onSettings={() => navigate('/settings/users')}
        onSignOut={logout}
      />
    </header>
  );
}

/**
 * Who you are, and the way out.
 *
 * The organization is shown next to the name rather than tucked inside the menu.
 * With several tenants and repeated usernames, "which organization am I looking
 * at?" is a question that has to be answerable at a glance — mistaking one for
 * another is how someone codes a survey against the wrong team's prompts.
 */
function AccountMenu({ user, organization, isAdmin, onSettings, onSignOut }) {
  if (!user) return null;
  const name = user.display_name || user.username;

  return (
    <Dropdown
      align="right"
      trigger={({ toggle }) => (
        <button
          className="account"
          type="button"
          onClick={toggle}
          title={`${user.username} · ${user.role}`}
        >
          <span className="account__avatar" aria-hidden="true">
            {name.slice(0, 1).toUpperCase()}
          </span>
          <span className="account__text">
            <span className="account__name">{name}</span>
            {organization && <span className="account__org">{organization.name}</span>}
          </span>
        </button>
      )}
      render={({ close }) => (
        <>
          <div className="account__header">
            <div className="account__name">{name}</div>
            <div className="account__meta">
              {user.username} · {user.role}
            </div>
            {organization && <div className="account__meta">{organization.name}</div>}
          </div>
          <DropdownDivider />
          <DropdownItem
            onClick={() => {
              close();
              onSettings();
            }}
          >
            {isAdmin ? 'Users and API keys' : 'Your account'}
          </DropdownItem>
          <DropdownDivider />
          <DropdownItem
            onClick={() => {
              close();
              onSignOut();
            }}
          >
            Sign out
          </DropdownItem>
        </>
      )}
    />
  );
}
